/*
  Required data, set by hand (workflow 09; CP-13). Two small dialogs share this one:

  - "phase_setting": from which project phase a data field is required on this item, with a phase for each operating case
    when the field has cases. The line hangs on the field and names the item (a setting per field-and-item pair, DEC-1016-93).
  - "at_least_one": from which project phase at least one of a component (or of a link) is required.

  The framework only offers the choice. It never suggests a phase: when the data is needed is the team's decision
  (DEC-1016-91, DEC-1016-96 R2-21), so the box starts empty, or with what the line already says.
*/

import { useState } from "react";

import { refusalText } from "../../../api/errors";
import { useApiCommand, useApiQuery } from "../../../api/hooks";
import type { Id, Json, LineRow, Phase } from "../../../api/types";
import { Button, Dialog } from "../../../ui";
import { useCommandId, useRefresh } from "../hooks";
import { PHASES } from "../lib/words";
import { FormError, Select, TextInput } from "./fields";

type SettingKind = "phase_setting" | "at_least_one";

interface PhaseDialogProps {
  kind: SettingKind;
  /** The field (for a phase setting) or the component or link (for "at least one") the line hangs on. */
  owner: { id: Id; name: string };
  /** The item the field is read on. Needed for a phase setting. */
  itemId?: Id;
  /** The field has one value for each operating case, so each case may have its own phase. */
  perCase?: boolean;
  /** The project phases of the discipline, in order. */
  phases?: readonly Phase[];
  onClose: () => void;
}

const GONE = new Set(["withdrawn", "removed", "rejected"]);

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/** The line this dialog changes, when the element already has one for this pair. */
export function existingSetting(lines: readonly LineRow[], kind: SettingKind, ownerId: Id, itemId?: Id): LineRow | undefined {
  return lines.find((line) => {
    if (line.kind !== kind || (line.state && GONE.has(line.state))) return false;
    const content = record(line.content);
    return kind === "phase_setting" ? content.item_id === itemId : (content.target_id ?? ownerId) === ownerId;
  });
}

export function PhaseDialog({ kind, owner, itemId, perCase = false, phases, onClose }: PhaseDialogProps) {
  const view = useApiQuery("element", { id: owner.id });
  const line = view.data ? existingSetting(view.data.lines, kind, owner.id, itemId) : undefined;
  // The form is made from the line once it is read, and then belongs to the person.
  const [draft, setDraft] = useState<{ phase: string; cases: Array<{ name: string; phase: string }> } | null>(null);
  const content = record(line?.content);
  const phase = draft?.phase ?? (typeof content.phase === "string" ? content.phase : "");
  const cases =
    draft?.cases ??
    Object.entries(record(content.per_case)).map(([name, value]) => ({ name, phase: typeof value === "string" ? value : "" }));
  const change = (patch: Partial<{ phase: string; cases: Array<{ name: string; phase: string }> }>) => setDraft({ phase, cases, ...draft, ...patch });

  const [problem, setProblem] = useState<string | null>(null);
  const add = useApiCommand("add_line");
  const edit = useApiCommand("edit_line");
  const ids = useCommandId();
  const refresh = useRefresh();
  const saving = add.isPending || edit.isPending;
  const options = (phases && phases.length > 0 ? phases : PHASES).map((p) => ({ value: p, label: p }));
  const words = kind === "phase_setting" ? { title: `When is ${owner.name} required?`, label: "Required from" } : { title: `At least one ${owner.name}`, label: "At least one from" };

  const submit = () => {
    if (!phase) {
      setProblem(kind === "phase_setting" ? "Choose the phase from which it is required." : "Choose the phase from which at least one is needed.");
      return;
    }
    const named = cases.filter((entry) => entry.name.trim() || entry.phase);
    if (kind === "phase_setting" && named.some((entry) => !entry.name.trim() || !entry.phase)) {
      setProblem("Give every case a name and a phase, or take the row out.");
      return;
    }
    setProblem(null);
    const next: Record<string, unknown> =
      kind === "phase_setting"
        ? {
            ...content,
            field_id: owner.id,
            item_id: itemId,
            phase,
            per_case: perCase && named.length > 0 ? Object.fromEntries(named.map((entry) => [entry.name.trim(), entry.phase])) : null,
          }
        : { ...content, target_id: owner.id, phase, limit_origin: content.limit_origin ?? "our_team" };
    const done = {
      onSuccess: () => {
        ids.renew();
        refresh("element", "item_page", "item_parts", "inbox", "sme_card");
        onClose();
      },
    };
    const json = next as unknown as Json;
    if (line) edit.mutate({ id: line.id, expected_version: line.version, content: json, command_id: ids.current() }, done);
    else add.mutate({ id: owner.id, line: { client_key: `${kind}:${owner.id}`, kind, content: json }, command_id: ids.current() }, done);
  };

  return (
    <Dialog
      open
      closeOnBackdrop={false}
      onClose={onClose}
      title={words.title}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={saving} disabled={view.isPending} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      <p className="muted">
        {kind === "phase_setting"
          ? "You decide when this data is needed. The SME validates the setting with the field."
          : "You decide when at least one is needed. The SME validates the setting with the element."}
      </p>
      {view.isError && <FormError message={refusalText(view.error)} />}
      <Select label={words.label} required value={phase} placeholder="Choose a phase" options={options} error={problem && !phase ? problem : null} onChange={(value) => change({ phase: value })} />
      {kind === "phase_setting" && perCase && (
        <fieldset className="field field--group">
          <legend className="field__label">A different phase for an operating case (optional)</legend>
          {cases.map((entry, index) => (
            <div key={index} className="row">
              <TextInput label={`Case ${index + 1}: name`} value={entry.name} onChange={(value) => change({ cases: cases.map((c, i) => (i === index ? { ...c, name: value } : c)) })} />
              <Select
                label={`Case ${index + 1}: required from`}
                value={entry.phase}
                placeholder="Choose a phase"
                options={options}
                onChange={(value) => change({ cases: cases.map((c, i) => (i === index ? { ...c, phase: value } : c)) })}
              />
              <Button variant="quiet" onClick={() => change({ cases: cases.filter((_c, i) => i !== index) })} aria-label={`Take out case ${index + 1}`}>
                Take it out
              </Button>
            </div>
          ))}
          <div>
            <Button variant="quiet" onClick={() => change({ cases: [...cases, { name: "", phase: "" }] })}>
              Add a case
            </Button>
          </div>
        </fieldset>
      )}
      {problem && phase && <FormError message={problem} />}
      <FormError message={add.error ? refusalText(add.error) : edit.error ? refusalText(edit.error) : null} />
    </Dialog>
  );
}
